import React from 'react';
import { NumericFormat } from 'react-number-format';
import CampoValor from './CampoValor';
import CampoPercentual from './CampoPercentual';
import '../css/financeiro-parcela-pagar.css';

/**
 * Campos padrão do design system .fin-pp.
 *
 * Use SEMPRE estes componentes nas telas redesenhadas para garantir inputs
 * idênticos (tamanho, borda, cor, fonte) em toda tela/aba. Não usar
 * `form-control` / `form-floating` / inputs bootstrap crus.
 *
 * Layout: envolva cada controle em <FinField label="...">. Para select/data,
 * use os componentes existentes ReactSelect / Data DENTRO do FinField.
 *
 *   <FinField label="Nome" required>
 *     <FinInput value={x} onChange={...} />
 *   </FinField>
 *   <FinField label="Cliente">
 *     <ReactSelect ... />
 *   </FinField>
 */

/**
 * Ícone de ajuda ao lado do rótulo.
 *
 * Pedido do Financeiro (reunião 2026-08-06): "em todo lugar que precisa de
 * informação, tem um toast de informação pros usuários" — nasceu do caso do
 * código SAP, que ninguém sabia onde buscar ("mas aonde eu arrumo o SAP?").
 *
 * Usa o `title` nativo em vez de um popover próprio: funciona em teclado e
 * leitor de tela sem JS, não é recortado por `overflow:hidden` (o que quebraria
 * dentro de modal/card) e não precisa de posicionamento. O `aria-label` repete
 * o texto porque `title` sozinho não é anunciado de forma confiável.
 */
export function FinAjuda({ texto }) {
    const [pos, setPos] = React.useState(null);
    const ref = React.useRef(null);

    if (!texto) return null;

    // position:fixed em vez de absolute: `overflow:hidden` de um ancestral NAO
    // recorta elemento fixed, entao o balao funciona dentro de modal e card sem
    // precisar de portal. As coordenadas vem do proprio icone.
    const abrir = () => {
        const r = ref.current && ref.current.getBoundingClientRect();
        if (!r) return;
        setPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
    };
    const fechar = () => setPos(null);

    return (
        <>
            <span
                ref={ref}
                className="fin-pp__tip"
                role="button"
                tabIndex={0}
                aria-label={texto}
                onMouseEnter={abrir}
                onMouseLeave={fechar}
                onFocus={abrir}
                onBlur={fechar}
            >i</span>
            {pos && (
                <span className="fin-pp__tip-bolha" style={{ top: pos.top, left: pos.left }} role="tooltip">
                    {texto}
                </span>
            )}
        </>
    );
}

// Wrapper: rótulo em cima + controle. `full` ocupa 100% da linha; `grow` cresce.
// `ajuda` = texto do ⓘ ao lado do rótulo.
// `extra` = o que vai na mesma linha do rótulo, à direita (pill de status, checkbox de N/A),
// no .fin-pp__field-head. Sem ele o campo renderiza exatamente como antes.
export function FinField({ label, required = false, ajuda = null, htmlFor = null, full = false, grow = false, extra = null, className = '', style = null, children }) {
    const cls = 'fin-pp__field'
        + (full ? ' fin-pp__field--full' : '')
        + (grow ? ' fin-pp__field--grow' : '')
        + (className ? ' ' + className : '');
    const rotulo = label ? (
        <label className="fin-pp__label" htmlFor={htmlFor}>
            {label}{required ? <span style={{ color: 'var(--fin-dg)' }}> *</span> : null}
            <FinAjuda texto={ajuda} />
        </label>
    ) : null;
    return (
        <div className={cls} style={style}>
            {extra ? <div className="fin-pp__field-head">{rotulo}{extra}</div> : rotulo}
            {children}
        </div>
    );
}

// Input de texto/single-line (text, email, number nativo, date nativo, etc.)
export function FinInput({ className = '', style = null, ...props }) {
    return (
        <input
            className={'fin-pp__input' + (className ? ' ' + className : '')}
            style={{ paddingLeft: 14, ...(style || {}) }}
            {...props}
        />
    );
}

// Textarea
export function FinTextarea({ className = '', rows = 4, style = null, ...props }) {
    return (
        <textarea
            rows={rows}
            className={'fin-pp__input' + (className ? ' ' + className : '')}
            style={{ height: 'auto', padding: '10px 14px', ...(style || {}) }}
            {...props}
        />
    );
}

// Número formatado (R$, milhares, etc.) — NumericFormat com visual fin-pp
export function FinNumber({ className = '', style = null, thousandSeparator = '.', decimalSeparator = ',', decimalScale = 2, ...props }) {
    return (
        <NumericFormat
            className={'fin-pp__input' + (className ? ' ' + className : '')}
            style={{ paddingLeft: 14, ...(style || {}) }}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={decimalScale}
            {...props}
        />
    );
}

// Valor monetário / percentual com a máscara que funciona (QA 26/09): "1.500,00"
// digitado vira 1500, colagem inteligente, seleção no foco. Preferir estes ao
// FinNumber em campo de dinheiro/percentual. `moeda` = prefixo (real/dolar/euro).
export function FinValor({ className = '', style = null, ...props }) {
    return (
        <CampoValor
            className={'fin-pp__input' + (className ? ' ' + className : '')}
            style={{ paddingLeft: 14, ...(style || {}) }}
            {...props}
        />
    );
}

export function FinPercentual({ className = '', style = null, ...props }) {
    return (
        <CampoPercentual
            className={'fin-pp__input' + (className ? ' ' + className : '')}
            style={{ paddingLeft: 14, ...(style || {}) }}
            {...props}
        />
    );
}

// Checkbox / radio com rótulo em linha
export function FinCheck({ label = null, id = null, type = 'checkbox', disabled = false, className = '', ...props }) {
    return (
        <label className={'fin-pp__check-row' + (disabled ? ' is-disabled' : '') + (className ? ' ' + className : '')} htmlFor={id}>
            <input id={id} type={type} className="fin-pp__check" disabled={disabled} {...props} />
            {label ? <span className="fin-pp__check-label">{label}</span> : null}
        </label>
    );
}

// Switch liga/desliga com rótulo em linha — checkbox com aparência de trilho. Para
// escolha que abre ou fecha um bloco de campos; marcação em lista continua FinCheck.
export function FinSwitch({ label = null, id = null, disabled = false, className = '', ...props }) {
    return (
        <label className={'fin-pp__check-row' + (disabled ? ' is-disabled' : '') + (className ? ' ' + className : '')} htmlFor={id}>
            <input id={id} type="checkbox" role="switch" className="fin-pp__switch" disabled={disabled} {...props} />
            {label ? <span className="fin-pp__check-label">{label}</span> : null}
        </label>
    );
}

// Grupo de checks/radios (empilhado por padrão; inline opcional)
export function FinChecks({ inline = false, className = '', style = null, children }) {
    return (
        <div className={'fin-pp__checks' + (inline ? ' fin-pp__checks--inline' : '') + (className ? ' ' + className : '')} style={style}>
            {children}
        </div>
    );
}
